import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StoreService } from '../../core/services/store.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { CurrencyService } from '../../core/services/currency.service';
import { ReportService, ReportKind, ReportFormat } from './report.service';

interface ReportType {
  key: ReportKind;
  icon: string;
  /** Sana filtri qo'llanadimi (stock — joriy snapshot, yo'q) */
  dates: boolean;
}

function startOfMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    MultiSelectModule,
    SelectModule,
    ButtonModule,
    TranslatePipe,
  ],
  templateUrl: './reports.component.html',
})
export class ReportsComponent implements OnInit {
  private reportSvc = inject(ReportService);
  private storeSvc = inject(StoreService);
  private messages = inject(MessageService);
  private i18n = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly currencySvc = inject(CurrencyService);

  filterFrom: Date | null = startOfMonth();
  filterTo: Date | null = new Date();
  selectedStoreIds: string[] = [];
  /** Bo'sh — tenant kotirovka valyutasi (backend default). */
  selectedCurrency = '';

  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  /** Yuklanayotgan tugma kaliti: "kind:format" */
  busy = signal<string | null>(null);

  /** Kam qoldiq hisoboti chegarasi (default 10). */
  lowStockThreshold = 10;

  readonly reportTypes: ReportType[] = [
    { key: 'sales', icon: 'pi-shopping-cart', dates: true },
    { key: 'profit', icon: 'pi-chart-line', dates: true },
    { key: 'stock', icon: 'pi-box', dates: false },
    { key: 'low-stock', icon: 'pi-exclamation-triangle', dates: false },
    { key: 'debts', icon: 'pi-credit-card', dates: true },
  ];

  ngOnInit(): void {
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => this.stores.set(r.data ?? []),
        error: () => this.stores.set([]),
      });
  }

  /** Filtrlarni butunlay tozalaydi (sana va do'kon bo'sh bo'ladi). */
  clearFilters(): void {
    this.filterFrom = null;
    this.filterTo = null;
    this.selectedStoreIds = [];
    this.selectedCurrency = '';
    this.lowStockThreshold = 10;
    // PrimeNG ngModel'lari dasturiy o'zgarishni ko'rishi uchun CD'ni majburlaymiz.
    this.cdr.detectChanges();
  }

  isBusy(kind: ReportKind, format: ReportFormat): boolean {
    return this.busy() === `${kind}:${format}`;
  }

  anyBusy(): boolean {
    return this.busy() !== null;
  }

  download(kind: ReportKind, format: ReportFormat): void {
    if (this.anyBusy()) return;
    this.busy.set(`${kind}:${format}`);
    this.reportSvc
      .download(kind, format, {
        from: this.filterFrom ?? undefined,
        to: this.filterTo ?? undefined,
        storeIds: this.selectedStoreIds.length ? this.selectedStoreIds : undefined,
        threshold: this.lowStockThreshold,
        currency: this.selectedCurrency || undefined,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.busy.set(null)),
      )
      .subscribe({
        next: () =>
          this.messages.add({
            severity: 'success',
            summary: this.i18n.instant('API.TOAST.SUCCESS'),
            detail: this.i18n.instant('REPORTS.DONE'),
          }),
        error: () =>
          this.messages.add({
            severity: 'error',
            summary: this.i18n.instant('API.TOAST.ERROR'),
            detail: this.i18n.instant('REPORTS.FAILED'),
            life: 5000,
          }),
      });
  }
}
